import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Package, ShoppingCart, DollarSign, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, money, categoryName } from "@/lib/api";
import { AdminShell } from "@/pages/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
  };
  useEffect(load, []);

  const del = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const cards = [
    { label: "Total Products", value: stats?.total_products ?? "—", icon: Package },
    { label: "Total Orders", value: stats?.total_orders ?? "—", icon: ShoppingCart },
    { label: "Revenue", value: stats ? money(stats.revenue) : "—", icon: DollarSign },
    { label: "Out of Stock", value: stats?.out_of_stock ?? "—", icon: AlertTriangle },
  ];

  return (
    <AdminShell active="overview">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="border border-zinc-200 bg-white p-5" data-testid={`stat-${c.label}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-zinc-500">{c.label}</span>
              <c.icon size={16} className="text-kkaccent" />
            </div>
            <p className="font-display mt-3 text-2xl">{c.value}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="products" className="mt-8">
        <TabsList className="rounded-none bg-zinc-100">
          <TabsTrigger value="products" className="rounded-none" data-testid="tab-products">Products</TabsTrigger>
          <TabsTrigger value="orders" className="rounded-none" data-testid="tab-orders">Orders</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-lg">All Products ({products.length})</h2>
            <Link to="/admin/products/new">
              <Button className="rounded-none bg-zinc-900 text-xs uppercase tracking-widest" data-testid="add-product-btn">
                <Plus size={15} className="mr-1.5" /> Add Product
              </Button>
            </Link>
          </div>
          <div className="overflow-x-auto border border-zinc-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Price</th>
                  <th className="px-4 py-3">Stock</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {products.map((p) => (
                  <tr key={p.id} data-testid={`admin-product-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-10 shrink-0 bg-zinc-100">
                          {p.images?.[0] && <img src={imgUrl(p.images[0])} alt="" className="h-full w-full object-cover" />}
                        </div>
                        <span className="font-medium">{p.title}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-600">{categoryName(p.category)}</td>
                    <td className="px-4 py-3">
                      {p.sale_price ? (
                        <span><span className="text-kksale">{money(p.sale_price)}</span> <span className="text-zinc-400 line-through">{money(p.regular_price)}</span></span>
                      ) : money(p.regular_price)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs ${p.stock_status === "out_of_stock" ? "text-kksale" : "text-emerald-700"}`}>
                        {p.stock_status === "out_of_stock" ? "Out of stock" : `In stock (${p.stock_count})`}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/admin/products/${p.id}/edit`} className="text-zinc-500 hover:text-zinc-900" data-testid={`edit-${p.id}`}>
                          <Pencil size={15} />
                        </Link>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="text-zinc-500 hover:text-kksale" data-testid={`delete-${p.id}`}><Trash2 size={15} /></button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this product?</AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone. "{p.title}" will be permanently removed.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => del(p.id)} className="rounded-none bg-kksale hover:bg-kksale/90" data-testid={`confirm-delete-${p.id}`}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-5">
          <h2 className="font-display mb-4 text-lg">Orders ({orders.length})</h2>
          {orders.length === 0 ? (
            <div className="border border-zinc-200 bg-white py-16 text-center text-sm text-zinc-500">No paid orders yet.</div>
          ) : (
            <div className="overflow-x-auto border border-zinc-200 bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Items</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {orders.map((o) => (
                    <tr key={o.id} data-testid={`order-row-${o.id}`}>
                      <td className="px-4 py-3">{o.name || o.email || "Guest"}</td>
                      <td className="px-4 py-3 text-zinc-600">{(o.items || []).reduce((s, i) => s + i.quantity, 0)} items</td>
                      <td className="px-4 py-3 font-medium">{money(o.amount)}</td>
                      <td className="px-4 py-3"><span className="bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">{o.status}</span></td>
                      <td className="px-4 py-3 text-zinc-500">{new Date(o.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </AdminShell>
  );
}
